<script>
import RexButton from '@/rex-components/rex-button.vue';
import Loading from '@/rex-components/rex-loading.vue';
import { LOGService } from '@/services/api/log.service';
import { mapState, mapGetters } from 'vuex';
import {
  SOURCE,
  SQL,
  TEXT,
  VIEW_GROK_TAB_KEY,
  VIEW_SAVED_SEARCHES_TAB_KEY,
  ANALYTICS_SOURCE_QUERY_PREFIX,
} from '@/components/Log/log.constants';
import { notificationError } from '@/utils/notification';
import { getTimeZoneOffset } from '@/utils/time/timeZoneOffset';
import moment from 'dayjs';

export default {
  components: { RexButton, Loading },
  computed: {
    ...mapState('globalState/user', ['teamID']),
    ...mapState('globalState/settings', ['timezoneoffset']),
    ...mapGetters('globalState/settings', ['getDurationTime']),
    ...mapState('logState/logCommonState', ['freeSearchSources', 'isADType']),
    ...mapGetters('logState/logCommonState', ['getCapabilityType']),
    enableDownload() {
      return (
        (this.$route.query?.currView !== VIEW_SAVED_SEARCHES_TAB_KEY &&
          this.$route.query?.currView !== VIEW_GROK_TAB_KEY) ||
        this.$route.query?.queryExec?.length > 0
      );
    },
    downloadIconColor() {
      return !this.enableDownload ? 'rgb(45, 48, 50)' : 'rgb(245, 150, 0)';
    },
  },
  data() {
    return {
      queryStr: '',
      sourceInfo: '',
      tag: '',
      loading: false,
      pcntDownloaded: 0,
    };
  },
  methods: {
    getAPIDetails() {
      let params, fileName;
      if (this.queryStr?.length > 0) {
        if (this.$route.query.isSqlQuery !== 'false') {
          params = {
            type: SQL,
            sqlQueryRequest: {
              team_id: this.teamID,
              query: this.queryStr,
              duration: this.getDurationTime,
              timezone: getTimeZoneOffset(this.timezoneoffset),
              capability: this.getCapabilityType,
            },
            capability: this.getCapabilityType,
          };
          fileName = 'SixthSense_Query_Table_Results.csv';
        } else {
          params = {
            type: TEXT,
            textExecuteRequest: {
              timezone: getTimeZoneOffset(this.timezoneoffset),
              query: this.queryStr,
              // limit: 1,
              // offset: 0,
              team_id: this.teamID,
              sources: this.freeSearchSources,
              duration: this.getDurationTime,
            },
          };
          const date = new Date();
          const formattedDate = moment(date).format('YYYY-MM-DD HHmmss');
          fileName = `SixthSense LM - ${formattedDate}.csv`;
        }
      } else {
        params = {
          type: SOURCE,
          serviceTrafficDataRequest: {
            service_id: this.sourceInfo[0],
            tags: [this.tag],
            duration: this.getDurationTime,
            timezone: getTimeZoneOffset(this.timezoneoffset),
            capability: this.getCapabilityType,
          },
        };
        fileName = `SixthSense_${this.isADType ? 'Analytics' : 'Log'}_${this.sourceInfo[1]}.csv`;
      }

      return { params, fileName };
    },
    async onDownload() {
      try {
        this.loading = true;
        const { params, fileName } = this.getAPIDetails();
        const config = {
          onDownloadProgress: (event) => {
            this.pcntDownloaded = Math.round(
              Math.round(event.loaded * 100) / event.total,
            );
            if (event.loaded === event.total) this.pcntDownloaded = 0;
          },
        };

        const { data } = await LOGService.downloadLogs(params, config);

        const blob = new Blob([data], {
          type: 'data:text/csv;charset=utf-8,',
        });

        const elem = window.document.createElement('a');
        elem.href = window.URL.createObjectURL(blob);
        elem.download = fileName;
        document.body.appendChild(elem);
        elem.click();
        document.body.removeChild(elem);

        this.loading = false;
      } catch (error) {
        console.log(error);
        this.loading = false;
        notificationError(this.$t('log.errorWhileDownloadingLogs'));
      }
    },
  },
  watch: {
    '$route.query.queryExec': {
      immediate: true,
      handler(newValue) {
        this.queryStr = newValue;
      },
    },
    '$route.query.source': {
      immediate: true,
      handler(newValue) {
        this.sourceInfo = newValue?.split(',');
      },
    },
    '$route.query.tag': {
      immediate: true,
      handler(newValue) {
        this.tag = newValue;
      },
    },
  },
};
</script>

<template>
  <div>
    <RexButton
      class="logs-download-btn"
      :variant="enableDownload && !loading ? 'secondary' : 'disabled'"
      id="log-actions-download-logs"
      :styling="{ width: '10rem' }"
      @onClick="onDownload"
    >
      <div v-if="loading" class="pcnt-loader">
        <div class="pcnt-text">
          {{ `${pcntDownloaded}%` }}
        </div>
        <Loading />
      </div>
      <span v-else class="download-text-icon">
        <BaseIcon
          name="download"
          width="0.75rem"
          height="1rem"
          :iconColor="downloadIconColor"
          class="log-download-icon"
        />
        <span>
          {{ $t('log.download') }}
        </span>
      </span>
    </RexButton>
  </div>
</template>

<style lang="scss" scoped>
.logs-download-btn {
  margin-left: 1.25rem;
}
.log-download-icon {
  margin-right: 0.525rem;
}
.download-text-icon {
  @include flex-center;
}
.pcnt-loader {
  @include flex-center;
}
.pcnt-text {
  margin-right: 0.75rem;
  color: $font-primary;
}
:deep(.loader01) {
  height: 1.5rem !important;
  width: 1.5rem !important;
  border: 0.15rem solid $color-white;
  border-right-color: transparent;
}
:deep(.loader01:after) {
  height: 0 !important;
  width: 0 !important;
}
</style>
